import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

// In-place re-login modal. When the backend session expires mid-use, the API
// interceptor dispatches a `session-expired` window event instead of letting
// the app bounce to the login page. This modal lets the user re-authenticate
// without leaving the current page — so they don't lose their place / have to
// redo their work. On success the new token is stored and the modal closes; the
// page underneath stays exactly as it was.
export default function SessionExpiredModal() {
  const { user, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const onExpired = () => {
      setOpen((prev) => {
        if (!prev) { setPassword(''); setErr(''); }
        return true; // ignore repeat events while already open
      });
    };
    window.addEventListener('session-expired', onExpired);
    return () => window.removeEventListener('session-expired', onExpired);
  }, []);

  // Prefill the email from the still-in-memory user so they only type a password.
  useEffect(() => {
    if (open && user?.email) setEmail(user.email);
  }, [open, user]);

  const submit = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!email.trim() || !password) { setErr('请输入邮箱和密码 / Enter email and password'); return; }
    setSubmitting(true);
    setErr('');
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (res && res.success) {
      setOpen(false);
      setPassword('');
    } else {
      setErr((res && res.error) || '登录失败，请重试 / Login failed, try again');
    }
  }, [email, password, login]);

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2147483646, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 380, maxWidth: '94vw', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div style={{ background: '#3b3b3b', color: '#fff', padding: '18px 22px' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>会话已过期，请重新登录</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>Session expired — sign in to continue (you'll stay on this page)</div>
        </div>
        <form onSubmit={submit} style={{ padding: '20px 22px 22px' }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>邮箱 / Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 14, outline: 'none' }} />

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>密码 / Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" autoFocus
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, marginBottom: 14, outline: 'none' }} />

          {err && <div style={{ color: '#dc2626', fontSize: 12.5, marginBottom: 12 }}>{err}</div>}

          <button type="submit" disabled={submitting}
            style={{ width: '100%', padding: '12px 0', borderRadius: 8, border: 'none', background: '#722F37', color: '#fff', fontSize: 15, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
            {submitting ? '登录中…' : '重新登录 / Sign in'}
          </button>

          <button type="button" onClick={() => { window.location.href = `${process.env.PUBLIC_URL}/login`; }}
            style={{ width: '100%', marginTop: 10, padding: '8px 0', borderRadius: 8, border: 'none', background: 'transparent', color: '#6b7280', fontSize: 12.5, cursor: 'pointer' }}>
            返回登录页面 / Go to login page
          </button>
        </form>
      </div>
    </div>
  );
}
